import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Plus, Trash2, Save, ArrowLeft, Settings, Eye, EyeOff,
  GripVertical, CheckCircle, AlertCircle, Clock, Award
} from 'lucide-react';
import * as api from '../../api/educentialApi';
import QuestionEditor from './QuestionEditor';
import './QuizBuilder.css';

function QuizBuilder() {
  const { lessonId, quizId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [quiz, setQuiz] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [notification, setNotification] = useState(null);

  const [quizSettings, setQuizSettings] = useState({
    title: '',
    description: '',
    instructions: '',
    timeLimit: null,
    passingScore: 70,
    maxAttempts: null,
    shuffleQuestions: false,
    shuffleOptions: false,
    showAnswers: true
  });

  useEffect(() => {
    loadQuiz();
  }, [lessonId, quizId]);

  const loadQuiz = async () => {
    try {
      setLoading(true);
      if (quizId) {
        // Edit existing quiz
        const response = await api.getQuiz(quizId, true);
        if (response.success) {
          setQuiz(response.data);
          setQuizSettings({
            title: response.data.title,
            description: response.data.description || '',
            instructions: response.data.instructions || '',
            timeLimit: response.data.timeLimit,
            passingScore: response.data.passingScore,
            maxAttempts: response.data.maxAttempts,
            shuffleQuestions: response.data.shuffleQuestions,
            shuffleOptions: response.data.shuffleOptions,
            showAnswers: response.data.showAnswers
          });
          setQuestions(response.data.Questions || []);
        }
      } else {
        // New quiz - set defaults
        setQuizSettings({
          title: 'New Quiz',
          description: '',
          instructions: 'Answer all questions to the best of your ability.',
          timeLimit: null,
          passingScore: 70,
          maxAttempts: null,
          shuffleQuestions: false,
          shuffleOptions: false,
          showAnswers: true
        });
      }
    } catch (error) {
      console.error('Load quiz error:', error);
      showNotification('error', 'Failed to load quiz');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveQuiz = async () => {
    try {
      setSaving(true);

      // Save or create quiz
      let savedQuiz;
      if (quizId) {
        const response = await api.updateQuiz(quizId, quizSettings);
        savedQuiz = response.data;
      } else {
        const response = await api.createQuiz(lessonId, quizSettings);
        savedQuiz = response.data;
      }

      setQuiz(savedQuiz);
      showNotification('success', 'Quiz saved successfully!');

      // Redirect to edit mode if creating new quiz
      if (!quizId) {
        navigate(`/educator/quiz/${savedQuiz.id}/edit`, { replace: true });
      }
    } catch (error) {
      console.error('Save quiz error:', error);
      showNotification('error', 'Failed to save quiz');
    } finally {
      setSaving(false);
    }
  };

  const handleAddQuestion = async () => {
    if (!quiz?.id && !quizId) {
      showNotification('error', 'Please save the quiz first before adding questions');
      return;
    }

    const newQuestion = {
      type: 'MULTIPLE_CHOICE',
      question: '',
      explanation: '',
      points: 1,
      order: questions.length + 1,
      options: [
        { text: 'Option A', isCorrect: false },
        { text: 'Option B', isCorrect: false },
        { text: 'Option C', isCorrect: false },
        { text: 'Option D', isCorrect: false }
      ]
    };

    try {
      const response = await api.createQuestion(quiz?.id || quizId, newQuestion);
      if (response.success) {
        setQuestions([...questions, response.data]);
        showNotification('success', 'Question added');
      }
    } catch (error) {
      console.error('Add question error:', error);
      showNotification('error', 'Failed to add question');
    }
  };

  const handleUpdateQuestion = async (questionId, data) => {
    try {
      const response = await api.updateQuestion(questionId, data);
      if (response.success) {
        setQuestions(questions.map(q =>
          q.id === questionId ? response.data : q
        ));
        showNotification('success', 'Question updated');
      }
    } catch (error) {
      console.error('Update question error:', error);
      showNotification('error', 'Failed to update question');
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;

    try {
      await api.deleteQuestion(questionId);
      setQuestions(questions.filter(q => q.id !== questionId));
      showNotification('success', 'Question deleted');
    } catch (error) {
      console.error('Delete question error:', error);
      showNotification('error', 'Failed to delete question');
    }
  };

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 3000);
  };

  if (loading) {
    return (
      <div className="quiz-builder-container">
        <div className="loading-state">Loading quiz builder...</div>
      </div>
    );
  }

  return (
    <div className="quiz-builder-container">
      {/* Notification */}
      {notification && (
        <div className={`notification-banner ${notification.type}`}>
          <div className="notification-content">
            {notification.type === 'success' && <CheckCircle className="w-5 h-5" />}
            {notification.type === 'error' && <AlertCircle className="w-5 h-5" />}
            <span>{notification.message}</span>
          </div>
          <button onClick={() => setNotification(null)} className="notification-close">×</button>
        </div>
      )}

      {/* Header */}
      <div className="quiz-builder-header">
        <button className="btn-back" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4" />
          <span>Back</span>
        </button>
        <h1>Quiz Builder</h1>
        <div className="header-actions">
          <button
            className="btn-settings"
            onClick={() => setShowSettings(!showSettings)}
          >
            <Settings className="w-5 h-5" />
            <span>Settings</span>
          </button>
          <button
            className="btn-save"
            onClick={handleSaveQuiz}
            disabled={saving}
          >
            <Save className="w-5 h-5" />
            <span>{saving ? 'Saving...' : 'Save Quiz'}</span>
          </button>
        </div>
      </div>

      <div className="quiz-builder-content">
        {/* Quiz Settings Panel */}
        {showSettings && (
          <div className="settings-panel">
            <h2>Quiz Settings</h2>

            <div className="form-group">
              <label>Quiz Title *</label>
              <input
                type="text"
                value={quizSettings.title}
                onChange={(e) => setQuizSettings({ ...quizSettings, title: e.target.value })}
                placeholder="Enter quiz title"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                value={quizSettings.description}
                onChange={(e) => setQuizSettings({ ...quizSettings, description: e.target.value })}
                placeholder="Brief description of the quiz"
                className="form-textarea"
                rows="3"
              />
            </div>

            <div className="form-group">
              <label>Instructions</label>
              <textarea
                value={quizSettings.instructions}
                onChange={(e) => setQuizSettings({ ...quizSettings, instructions: e.target.value })}
                placeholder="Instructions for students"
                className="form-textarea"
                rows="3"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  <Clock className="w-4 h-4 inline" /> Time Limit (minutes)
                </label>
                <input
                  type="number"
                  value={quizSettings.timeLimit || ''}
                  onChange={(e) => setQuizSettings({
                    ...quizSettings,
                    timeLimit: e.target.value ? parseInt(e.target.value) : null
                  })}
                  placeholder="No limit"
                  className="form-input"
                  min="1"
                />
              </div>

              <div className="form-group">
                <label>
                  <Award className="w-4 h-4 inline" /> Passing Score (%)
                </label>
                <input
                  type="number"
                  value={quizSettings.passingScore}
                  onChange={(e) => setQuizSettings({
                    ...quizSettings,
                    passingScore: parseInt(e.target.value)
                  })}
                  className="form-input"
                  min="0"
                  max="100"
                />
              </div>

              <div className="form-group">
                <label>Max Attempts</label>
                <input
                  type="number"
                  value={quizSettings.maxAttempts || ''}
                  onChange={(e) => setQuizSettings({
                    ...quizSettings,
                    maxAttempts: e.target.value ? parseInt(e.target.value) : null
                  })}
                  placeholder="Unlimited"
                  className="form-input"
                  min="1"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={quizSettings.shuffleQuestions}
                  onChange={(e) => setQuizSettings({
                    ...quizSettings,
                    shuffleQuestions: e.target.checked
                  })}
                />
                <span>Shuffle question order for each student</span>
              </label>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={quizSettings.shuffleOptions}
                  onChange={(e) => setQuizSettings({
                    ...quizSettings,
                    shuffleOptions: e.target.checked
                  })}
                />
                <span>Shuffle answer options</span>
              </label>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={quizSettings.showAnswers}
                  onChange={(e) => setQuizSettings({
                    ...quizSettings,
                    showAnswers: e.target.checked
                  })}
                />
                <span>Show correct answers after submission</span>
              </label>
            </div>
          </div>
        )}

        {/* Questions List */}
        <div className="questions-section">
          <div className="section-header">
            <h2>Questions ({questions.length})</h2>
            <button className="btn-add-question" onClick={handleAddQuestion}>
              <Plus className="w-5 h-5" />
              <span>Add Question</span>
            </button>
          </div>

          {questions.length === 0 ? (
            <div className="empty-state">
              <p>No questions yet. Click "Add Question" to get started.</p>
            </div>
          ) : (
            <div className="questions-list">
              {questions.map((question, index) => (
                <QuestionEditor
                  key={question.id}
                  question={question}
                  questionNumber={index + 1}
                  onUpdate={handleUpdateQuestion}
                  onDelete={handleDeleteQuestion}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default QuizBuilder;
